import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import clsx from 'clsx';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';

import { type BasicSourceTypeDTO, sourceTypeBasicAtom } from '../index';
import SOURCE_TYPE, {
  isReadOnlyManagedSourceType
} from '../SourceCreate.constants';
import { useFetchSupportedLogSources } from '../SourceCreate.service';
import { supportedLogSourcesAtom } from '../SourceCreate.store';
import useStyles from './SourceSelectionStep.styles';
import type {
  CloudSource,
  SourceSelectionStepProps
} from './SourceSelectionStep.types';
import {
  extractLogSourcesFromDataSources,
  flattenCloudSources,
  mapAzureEventHubSources,
  sortFlattenedSources
} from './SourceSelectionStep.utils';

/** Search input placeholder text */
const SEARCH_PLACEHOLDER = 'Search sources...';

/**
 * Source Selection Step - First step in the cloud source creation wizard.
 *
 * Displays a searchable, filterable table of available cloud sources. Sources can be:
 * - Simple parent sources (e.g., AWS, CiscoUmbrella)
 * - Parent sources with child data providers (e.g., Microsoft Entra ID with Graph API, O365 API)
 *
 * Business Rules:
 * - Azure Event Hub sources are fetched separately via API
 * - Sources with multiple data providers are expanded into separate selectable rows
 * - Child sources may have vendor overrides (e.g., Microsoft vs specific product vendors)
 * - Ingestion method display is context-aware for child sources
 */
const SourceSelectionStep = ({
  selectedSource,
  onSourceSelect
}: SourceSelectionStepProps) => {
  const { classes } = useStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceTypeBasicData] = useAtom(sourceTypeBasicAtom);

  // Azure Event Hub requires separate API call for supported log sources
  useFetchSupportedLogSources(SOURCE_TYPE.AZURE_EVENT_HUB);
  const supportedLogSources = useAtomValue(supportedLogSourcesAtom);

  /**
   * Transforms backend source type data into frontend CloudSource format.
   *
   * Business Rule: Azure Event Hub log sources come from a separate API call,
   * while other sources extract log sources from their data_sources field.
   */
  const cloudSources = useMemo(() => {
    return sourceTypeBasicData
      .filter(
        (sourceType) =>
          sourceType.parent_source_type_id === null &&
          !isReadOnlyManagedSourceType(sourceType.id)
      )
      .map((sourceType: BasicSourceTypeDTO): CloudSource => {
        const isAzureEventHub = sourceType.id === SOURCE_TYPE.AZURE_EVENT_HUB;

        // Azure Event Hub uses dynamically fetched log sources
        // Other sources use their embedded data_sources structure
        const logSources =
          isAzureEventHub && supportedLogSources
            ? mapAzureEventHubSources(supportedLogSources)
            : extractLogSourcesFromDataSources(
                sourceType.data_sources || undefined
              );

        return {
          source_type_id: sourceType.id,
          name: sourceType.name,
          vendor: sourceType.vendor || '',
          product: sourceType.name,
          ingestion_method: sourceType.ingestion_method || 'Cloud API',
          cloud_api_doc_url: '',
          supported_log_sources: logSources.length ? logSources : undefined,
          data_sources: sourceType.data_sources || undefined
        };
      });
  }, [sourceTypeBasicData, supportedLogSources]);

  /**
   * Expands parent sources with children into separate selectable rows.
   *
   * Example: "Microsoft Entra ID" becomes multiple rows:
   * - Microsoft Entra ID - Graph API
   * - Microsoft Entra ID - O365 API
   *
   * Then sorts by vendor and display name for consistent ordering.
   */
  const flattenedSources = useMemo(() => {
    const flattened = flattenCloudSources(cloudSources);
    return sortFlattenedSources(flattened);
  }, [cloudSources]);

  /**
   * Filters sources based on user search input.
   *
   * Searches across: vendor, product, display name, and ingestion method.
   */
  const filteredSources = useMemo(() => {
    if (!searchTerm) return flattenedSources;

    const searchLower = searchTerm.toLowerCase();

    return flattenedSources.filter((sourceItem) => {
      const { source, displayName } = sourceItem;

      return (
        source.name.toLowerCase().includes(searchLower) ||
        source.vendor.toLowerCase().includes(searchLower) ||
        source.product.toLowerCase().includes(searchLower) ||
        displayName.toLowerCase().includes(searchLower) ||
        source.ingestion_method.toLowerCase().includes(searchLower)
      );
    });
  }, [flattenedSources, searchTerm]);

  /**
   * Handles selection of a parent source (no data providers).
   *
   * Example: User selects "AWS CloudTrail" directly
   */
  const handleParentSelect = (source: CloudSource) => {
    onSourceSelect({
      source_type_id: source.source_type_id,
      source_name: source.product,
      vendor: source.vendor,
      product: source.product,
      is_parent: true
    });
  };

  /**
   * Handles selection of a child data provider.
   *
   * Business Rule: Child sources can override the parent vendor.
   * Example: "Microsoft Entra ID - Graph API" where "Graph API" is the child
   *
   * @param vendorOverride - Child-specific vendor (overrides parent vendor if different)
   */
  const handleChildSelect = (
    source: CloudSource,
    childName: string,
    vendorOverride?: string
  ) => {
    onSourceSelect({
      source_type_id: source.source_type_id,
      source_name: `${source.product} - ${childName}`,
      vendor: vendorOverride || source.vendor,
      product: source.product,
      data_provider: childName,
      is_parent: false
    });
  };

  /**
   * Checks if a parent source (without data provider) is currently selected.
   */
  const isParentSelected = (sourceTypeId: number) => {
    return (
      selectedSource?.source_type_id === sourceTypeId &&
      selectedSource?.is_parent === true &&
      !selectedSource?.data_provider
    );
  };

  /**
   * Checks if a specific child data provider is currently selected.
   */
  const isChildSelected = (sourceTypeId: number, childName: string) => {
    return (
      selectedSource?.source_type_id === sourceTypeId &&
      selectedSource?.data_provider === childName &&
      selectedSource?.is_parent === false
    );
  };

  /**
   * Formats ingestion method display for child sources.
   *
   * Business Rule: Child sources with "Cloud Streaming/Storage" should
   * display "Via [Parent Product] Cloud Streaming/Storage" to clarify
   * the data flow path.
   *
   * Example: "Via Microsoft Entra ID Cloud Streaming/Storage"
   */
  const getIngestionMethodDisplay = (source: CloudSource, isChild: boolean) => {
    const isCloudStreaming =
      source.ingestion_method === 'Cloud Streaming/Storage';

    if (isCloudStreaming && source.product && isChild) {
      return `Via ${source.product}  Cloud Streaming/Storage`;
    }

    return source.ingestion_method;
  };

  return (
    <Box>
      <TextField
        className={classes.searchBox}
        fullWidth
        placeholder={SEARCH_PLACEHOLDER}
        value={searchTerm}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearchTerm(e.target.value)
        }
        size="small"
      />

      <TableContainer component={Paper} className={classes.sourceList}>
        {filteredSources.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography>No sources found</Typography>
          </Box>
        ) : (
          <Table stickyHeader className={classes.sourceSelectionTable}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>
                  <Typography>Vendor</Typography>
                </TableCell>
                <TableCell className={classes.headerCell}>
                  <Typography>Product</Typography>
                </TableCell>
                <TableCell className={classes.headerCell}>
                  <Typography>Ingestion Method</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSources.map((sourceItem) => {
                const {
                  source,
                  displayName,
                  isChild,
                  childName,
                  vendorOverride
                } = sourceItem;

                const isSelected = isChild
                  ? isChildSelected(source.source_type_id, childName!)
                  : isParentSelected(source.source_type_id);

                const handleRowClick = () => {
                  if (isChild) {
                    handleChildSelect(source, childName!, vendorOverride);
                  } else {
                    handleParentSelect(source);
                  }
                };

                // Use child-specific vendor if available, otherwise use parent vendor
                const displayVendor = vendorOverride || source.vendor;

                return (
                  <TableRow
                    key={`${source.source_type_id}-${displayName}`}
                    className={clsx(classes.sourceRow, {
                      selected: isSelected
                    })}
                    onClick={handleRowClick}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell className={classes.sourceCell}>
                      <Typography>{displayVendor}</Typography>
                    </TableCell>
                    <TableCell className={classes.sourceCell}>
                      <Typography>{displayName}</Typography>
                    </TableCell>
                    <TableCell className={classes.sourceCell}>
                      <Typography>
                        {getIngestionMethodDisplay(source, isChild)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default SourceSelectionStep;
